import { WorkBook, WorkSheet, CellObject } from 'xlsx'
import {
  record,
  array,
  option,
  tuple,
  either,
  nonEmptyArray,
  semigroup,
} from 'fp-ts'
import { pipe, flow, tuple as fTuple } from 'fp-ts/lib/function'
import { AssignableKeys } from '../components/Upload'

type CleanWorkSheet = Record<string, CellObject>

const firstRowTester = /^([A-Za-z]+)1$/
const rowAndColumnMatcher = /^([A-Za-z]+)([0-9]+)$/

const getColumnAndRowFromName = (
  cellName: string
): option.Option<{ column: string; row: string }> => {
  const gs = rowAndColumnMatcher.exec(cellName)
  if (!gs || gs.length < 3) {
    return option.none
  }
  return option.some({ column: gs[1], row: gs[2] })
}

const getColumnFromName = (cellName: string): option.Option<string> => {
  const gs = rowAndColumnMatcher.exec(cellName)
  if (!gs || gs.length < 3) {
    return option.none
  }
  return option.some(gs[1])
}

const getRowFromName = (cellName: string): option.Option<string> => {
  const gs = rowAndColumnMatcher.exec(cellName)
  if (!gs || gs.length < 3) {
    return option.none
  }
  return option.some(gs[2])
}

const isCellObject = (x: any): x is CellObject => !!x.v

const cleanUpWorkSheet: (ws: WorkSheet) => CleanWorkSheet =
  record.filter(isCellObject)

const getCellAsString = (c: CellObject) =>
  typeof c.v === 'string' ? option.some(c.v) : option.fromNullable(c.w)

const getCellValue = (c: CellObject) => {
  const cv = option.fromNullable(c.v)

  return pipe(
    option.fromNullable(c.w),
    option.chain(w =>
      pipe(
        cv,
        option.map(v => (typeof v === 'string' ? v : `${v}` !== w ? w : v))
      )
    ),
    option.alt(() => cv)
  )
}

export const sheetFirstRow = flow(
  record.filter(isCellObject),
  record.filterWithIndex(ind => firstRowTester.test(ind)),
  record.filterMap(getCellAsString)
)

const matchColumn = (col: string) =>
  flow(
    getColumnFromName,
    option.fold(
      () => false,
      v => col === v
    )
  )

const keyToIndex = flow(
  getRowFromName,
  option.map(v => parseInt(v, 10)),
  option.filter(v => !isNaN(v))
)

type GetColumnError = { err: 'GetColumnError'; column: string }

export type DataPoint = number | string | Date | boolean
type CellValue = [number, DataPoint]

export const getColumn =
  (headerCell: string) =>
  (s: CleanWorkSheet): either.Either<GetColumnError, CellValue[]> =>
    pipe(
      getColumnFromName(headerCell),
      option.map(col =>
        pipe(
          s,
          record.filterWithIndex(matchColumn(col)),
          record.filterWithIndex(ind => ind !== headerCell),
          record.filterMap(c => option.fromNullable(c.w ?? c.v)),
          record.toArray,
          array.map(tuple.mapFst(keyToIndex)),
          array.map(tuple.Traversable.sequence(option.Monad)),
          array.compact
        )
      ),
      either.fromOption(() => ({ err: 'GetColumnError', column: headerCell }))
    )

export type ExtractedData = Record<
  string,
  nonEmptyArray.NonEmptyArray<DataPoint>
>

const seperateByColumns = (s: CleanWorkSheet): ExtractedData => {
  const seperated: Record<string, (CellObject & { row: string })[]> = {}
  for (const k in s) {
    const maybeCol = getColumnAndRowFromName(k)

    if (option.isSome(maybeCol)) {
      const {
        value: { column, row },
      } = maybeCol
      seperated[column] = [...(seperated[column] ?? []), { ...s[k], row }]
    }
  }

  return pipe(
    seperated,
    record.filterMap(v => {
      if (v.length === 0) {
        return option.none
      }
      const [title, ...values] = v

      return option.some({
        title: pipe(
          getCellAsString(title),
          option.getOrElse(() => '')
        ),
        values: pipe(values, array.filterMap(getCellValue)),
      })
    }),
    record.collect((_, { title, values }) => fTuple(title, values)),
    record.fromFoldable(semigroup.first<DataPoint[]>(), array.Foldable),
    record.filter(array.isNonEmpty)
  )
}

const getSheets = (b: WorkBook): Record<string, CleanWorkSheet> =>
  pipe(
    b.SheetNames,
    array.map(n =>
      pipe(
        record.lookup(n, b.Sheets),
        option.map(cleanUpWorkSheet),
        option.map(sheet => fTuple(n, sheet))
      )
    ),
    array.compact,
    record.fromFoldable(semigroup.first<CleanWorkSheet>(), array.Foldable)
  )

export const extractDataFromWorkBook: (
  _: WorkBook
) => either.Either<Error, Record<string, ExtractedData>> = flow(
  getSheets,
  record.map(seperateByColumns),
  either.fromPredicate(
    r => !record.isEmpty(r),
    () => new Error('no valid data found')
  )
)

export type ExtractedObject = Record<string, DataPoint>

export const lengthMonoid = option.getMonoid({ concat: Math.min })

export const extractedDataToObjects = (d: ExtractedData): ExtractedObject[] =>
  pipe(
    d,
    record.foldMap(lengthMonoid)(v => option.some(v.length)),
    option.map(minLength => array.range(0, Math.max(minLength - 1, 0))),
    option.map(
      array.map(i =>
        pipe(
          d,
          record.map(vs => vs[i])
        )
      )
    ),
    option.Traversable.sequence(array.Monad),
    array.compact
  )

const billKeys = (mode: 'organization' | 'supplier'): AssignableKeys[] =>
  mode === 'organization'
    ? [
        'amount',
        'reference',
        'createdAt',
        'dueAt',
        'payerName',
        'payerEmail',
        'payerPhonenumber',
        'payerAddress',
        'taxRate',
        'taxName',
        'discountName',
        'discountRate',
      ]
    : [
        'amount',
        'reference',
        'createdAt',
        'dueAt',
        'organization',
        'email',
        'taxRate',
        'taxName',
        'discountName',
        'discountRate',
      ]

const keyAssignableValues: Record<AssignableKeys, Set<string>> = {
  amount: new Set(['amount']),
  reference: new Set(['reference', 'id', 'ref']),
  createdAt: new Set([
    'createdat',
    'created_at',
    'createddate',
    'created_date',
    'created',
  ]),
  dueAt: new Set(['dueat', 'due_at', 'duedate', 'due_date', 'due']),
  payerName: new Set([
    'payername',
    'payer_name',
    'payer',
    'customer_name',
    'customername',
    'customer',
  ]),
  payerEmail: new Set(['payeremail', 'payer_email', 'customer_email']),
  payerPhonenumber: new Set([
    'payerphonenumber',
    'payer_phonenumber',
    'payer_phone',
    'payer_number',
    'customer_phonenumber',
    'customer_phone',
    'customer_phone_number',
    'customer_number',
    'phone',
    'phone_number',
    'phonenumber',
  ]),
  payerAddress: new Set([
    'payeraddress',
    'address',
    'payer_address',
    'customer_address',
    'customer_address',
  ]),
  payerTaxNumber: new Set([
    'payertaxnumber',
    'taxnumber',
    'payer_tax_number',
    'payer_taxnumber',
    'customer_tax_number',
    'customer_taxnumber',
  ]),

  discountName: new Set(['discountname', 'discount_name']),
  discountRate: new Set(['discountrate', 'discount_rate']),

  taxName: new Set(['taxname', 'tax_name']),
  taxRate: new Set(['taxrate', 'tax_rate']),

  organization: new Set(['organization', 'org']),
  email: new Set(['email']),
}

export const defaultMapping = (
  mode: 'organization' | 'supplier',
  d: ExtractedData
): Record<AssignableKeys, string> =>
  pipe(
    billKeys(mode),
    array.map(k =>
      pipe(
        record.keys(d),
        array.findFirst(x => keyAssignableValues[k].has(x.toLowerCase())),
        option.map(x => fTuple(k, x))
      )
    ),
    array.compact,
    record.fromFoldable(semigroup.first<string>(), array.Foldable)
  )
